/**
 * cashModel — calcul partagé du Cash Outlook (trésorerie 12 mois).
 *
 * Extrait des memos de TreasuryPage pour être réutilisé à l'identique par la
 * page ET par l'impression (CashForecastPrint) → garantit des chiffres
 * rigoureusement identiques entre l'écran et le PDF.
 *
 * `computeCashModel` est PUR : aucune requête, aucun setState. Il prend les
 * données déjà chargées (scénario + sous-entités + données HR) et renvoie les
 * 5 sorties consommées par SynthesisTab. Lève si le moteur échoue — au
 * caller de gérer (la page met un message d'erreur, le print affiche un vide).
 */
import { calculerScenario } from './computeScenario';
import { composeEngineScenario } from './adapters';
import { computePayrollFromHr, grossForMonth } from './computePayrollFromHr';
import { extraMonths } from '../../hr/salaryMass';

export function computeCashModel(raw) {
  const {
    scenario, tenant = null,
    revenues = [], variables = [], indirectCosts = [], oneOff = [],
    payrollChanges = [], bonuses = [], socialRates = [], insurances = [],
    openingReceivables = [], openingPayables = [], initialBalance = null,
    hrContracts = [], hrMovements = [], hrCsRateEmpl = 0, hrLppRates = [],
    csLignes = [], annualExtrasByYear = {}, vatRates = [],
    // Mode Manuel : séries personnel { personnelProjection, chargesSocialesMonthly,
    // assurancesMonthly } prêtes à l'emploi ; remplacent la projection People.
    personnelOverride = null,
  } = raw || {};

  if (!scenario) {
    return {
      calc: null, personnelProjection: null, chargesSocialesMonthly: null,
      assurancesMonthly: null, annualExtrasMonthly: null,
    };
  }

  const startMonth = Number.isInteger(scenario.start_month) ? scenario.start_month : 0;
  const startYear = scenario.start_year || new Date().getFullYear();
  // Config 13e/14e du tenant (mois de versement) → masse brute incl. 13e/14e.
  const extra = extraMonths(tenant);

  // ── Annual extras projetés mois par mois (ordre scénario) ──
  const annualExtrasMonthly = (() => {
    const varComp = Array(12).fill(0);
    const bonus = Array(12).fill(0);
    const expenses = Array(12).fill(0);
    // People expenses : total de l'année de DÉPART du scénario / 12, constant
    // sur les 12 mois (même si l'horizon déborde sur N+1).
    const expensesStart = Math.round(Number(annualExtrasByYear[startYear]?.expenses || 0) / 12);
    for (let k = 0; k < 12; k += 1) {
      const calYear = startYear + Math.floor((startMonth + k) / 12);
      const ex = annualExtrasByYear[calYear];
      expenses[k] = expensesStart;
      if (!ex) continue;
      varComp[k] = Math.round(ex.varComp / 12);
      bonus[k] = Math.round(ex.bonus / 12);
    }
    return { varComp, bonus, expenses };
  })();

  // ── Taux LPP employé moyen pondéré (même logique que HrCostSimulationPage) ──
  const hrLppEmplPct = (() => {
    if (!hrLppRates.length) return 0;
    const sumRef = hrLppRates.reduce((a, r) => a + Number(r.reference_salary || 0), 0);
    const sumEmpl = hrLppRates.reduce((a, r) => a + Number(r.lpp_employee_chf || 0), 0);
    return sumRef > 0 ? (sumEmpl / sumRef) * 100 : 0;
  })();

  // ── Projection nette du personnel (ordre scénario) ──
  const personnelProjection = computePayrollFromHr({
    contracts: hrContracts,
    movements: hrMovements,
    startMonth,
    startYear,
    emplRatePct: hrCsRateEmpl + hrLppEmplPct,
    extra,
  });

  // ── Cotisations de base (empr% + empl% sur le brut mensuel) ──
  const chargesSocialesMonthly = (() => {
    if (!personnelProjection?.gross) return null;
    const baseLines = (csLignes || []).filter((l) => l.section === 'base');
    const totalPct = baseLines.reduce(
      (a, l) => a + Number(l.taux_empr || 0) + Number(l.taux_empl || 0), 0,
    );
    if (totalPct <= 0) return null;
    return personnelProjection.gross.map((g) => Math.round((Number(g) || 0) * totalPct / 100));
  })();

  // ── Assurances (LPP/LAA/IJM…) — phasing par rubrique ──
  const assurancesMonthly = (() => {
    const assuranceLines = (csLignes || []).filter((l) => l.section === 'assurance');
    if (assuranceLines.length === 0) return null;
    const out = Array(12).fill(0);
    for (const l of assuranceLines) {
      const rate = Number(l.taux_total || 0) || (Number(l.taux_empr || 0) + Number(l.taux_empl || 0));
      if (rate <= 0) continue;
      const pm = Array.isArray(l.payment_months) && l.payment_months.length > 0 ? l.payment_months : null;
      if (!pm) continue;
      const periodMonths = 12 / pm.length;
      for (let k = 0; k < 12; k += 1) {
        const calMonth = (startMonth + k) % 12;
        const calYear = startYear + Math.floor((startMonth + k) / 12);
        if (!pm.includes(calMonth)) continue;
        const grossM = grossForMonth({ contracts: hrContracts, movements: hrMovements, year: calYear, monthIdx: calMonth, extra });
        out[k] += grossM * periodMonths * rate / 100;
      }
    }
    return out.map((v) => Math.round(v));
  })();

  // ── Moteur (lève en cas d'erreur) ──
  const engineScenario = composeEngineScenario(scenario, {
    revenues, variables, recurring: indirectCosts, oneOff,
    payrollChanges, bonuses, socialRates, insurances,
    openingReceivables, openingPayables, initialBalance, vatRates,
  }, tenant);
  const calc = calculerScenario(engineScenario);

  // Mode Manuel : remplace les séries personnel par la saisie manuelle ; pas
  // d'extras / frais séparés (les primes/bonus sont saisis en ajustements du brut).
  if (personnelOverride) {
    return {
      calc,
      personnelProjection: personnelOverride.personnelProjection,
      chargesSocialesMonthly: personnelOverride.chargesSocialesMonthly,
      assurancesMonthly: personnelOverride.assurancesMonthly,
      annualExtrasMonthly: { varComp: Array(12).fill(0), bonus: Array(12).fill(0), expenses: Array(12).fill(0) },
    };
  }

  return { calc, personnelProjection, chargesSocialesMonthly, assurancesMonthly, annualExtrasMonthly };
}
